import type { ISerialize } from '@/objects/ISerialize';
import { formatDateIso } from '@/utilities/dateutils';

export class IBUSportsEvent implements ISerialize {
    public readonly Description: string | null;

    public readonly EndDate: Date;

    public readonly EventID: string | null;

    public readonly EventStatus: string | null;

    public readonly FirstCompetitionDate: Date;

    public readonly IsActual: boolean;

    public readonly IsCurrent: boolean;

    public readonly Level: number;

    public readonly Nat: string | null;

    public readonly Notes: string | null;

    public readonly Organizer: string | null;

    public readonly ShortDescription: string | null;

    public readonly StartDate: Date;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Description?: string | null;
        EndDate?: Date;
        EventID?: string | null;
        EventStatus?: string | null;
        FirstCompetitionDate?: Date;
        IsActual?: boolean;
        IsCurrent?: boolean;
        Level?: number;
        Nat?: string | null;
        Notes?: string | null;
        Organizer?: string | null;
        ShortDescription?: string | null;
        StartDate?: Date;
    }) {
        this.Description = values.Description ?? null;

        this.EndDate = values.EndDate ?? new Date('1970-01-01T00:00:00.000');

        this.EventID = values.EventID ?? null;

        this.EventStatus = values.EventStatus ?? null;

        this.FirstCompetitionDate = values.FirstCompetitionDate ?? new Date('1970-01-01T00:00:00.000');

        this.IsActual = values.IsActual ?? false;

        this.IsCurrent = values.IsCurrent ?? false;

        this.Level = values.Level ?? 0;

        this.Nat = values.Nat ?? null;

        this.Notes = values.Notes ?? null;

        this.Organizer = values.Organizer ?? null;

        this.ShortDescription = values.ShortDescription ?? null;

        this.StartDate = values.StartDate ?? new Date('1970-01-01T00:00:00.000');
    }

    public serialize(): string {
        return (
            '{' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"EndDate": ${'"' + formatDateIso(this.EndDate) + '"'}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"EventStatus": ${JSON.stringify(this.EventStatus)}` +
            ',' +
            `"FirstCompetitionDate": ${'"' + formatDateIso(this.FirstCompetitionDate) + '"'}` +
            ',' +
            `"IsActual": ${JSON.stringify(this.IsActual)}` +
            ',' +
            `"IsCurrent": ${JSON.stringify(this.IsCurrent)}` +
            ',' +
            `"Level": ${JSON.stringify(this.Level)}` +
            ',' +
            `"Nat": ${JSON.stringify(this.Nat)}` +
            ',' +
            `"Notes": ${JSON.stringify(this.Notes)}` +
            ',' +
            `"Organizer": ${JSON.stringify(this.Organizer)}` +
            ',' +
            `"ShortDescription": ${JSON.stringify(this.ShortDescription)}` +
            ',' +
            `"StartDate": ${'"' + formatDateIso(this.StartDate) + '"'}` +
            '}'
        );
    }

    /**
     * Creates an instance of IBUSportsEvent from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {}): IBUSportsEvent {
        return new IBUSportsEvent(values);
    }

    public static _FromServerJson(json: any): IBUSportsEvent {
        return new IBUSportsEvent({
            Description: json.Description ?? '',
            EndDate: json.EndDate instanceof Date ? json.EndDate : new Date(json.EndDate),
            EventID: json.EventID ?? '',
            EventStatus: json.EventStatus ?? '',
            FirstCompetitionDate:
                json.FirstCompetitionDate instanceof Date ? json.FirstCompetitionDate : new Date(json.FirstCompetitionDate),
            IsActual: json.IsActual,
            IsCurrent: json.IsCurrent,
            Level: json.Level,
            Nat: json.Nat ?? '',
            Notes: json.Notes ?? '',
            Organizer: json.Organizer ?? '',
            ShortDescription: json.ShortDescription ?? '',
            StartDate: json.StartDate instanceof Date ? json.StartDate : new Date(json.StartDate),
        });
    }

    /**
     * Creates an instance of IBUSportsEvent from another instance of IBUSportsEvent.
     * @param other The IBUSportsEvent you want to clone
     * @returns A cloned IBUSportsEvent
     */
    public static FromOther(other: IBUSportsEvent): IBUSportsEvent {
        return IBUSportsEvent._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return IBUSportsEvent.FromOther(this);
    }

    static NAME_OF_CLASS = 'IBUSportsEvent';
}
